import React, { useRef } from 'react';
import { isAndroid, isMobile, isTablet } from 'react-device-detect';
import { MISSIONS_TYPES } from 'shared/screens/Missions/constants';
import useMissionsTabs from 'shared/screens/Missions/hooks/useMissionsTabs';
import { tutorialOpacity } from 'shared/utils/tutorial';
import styled, { css } from 'styled-components';
import TimeRemainingText from '../../../components/TimeRemainingText';
import notificationCalculator from 'shared/screens/Missions/utils/notificationCalculator';
import NotificationBadge from '../../../components/NotificationBadge/NotificationBadge';

const MissionTabs = props => {
  const missionTabRef = useRef({});
  const { tutorialStep, activeTutorial, battlesTimestamp, noAvailableGames, battlesFeatureState, currentMissionType } =
    props;
  const { battlesAssets, selectedTab, handleOnPress, isOnMissions, isOnBattles, hideClockIcon, showClockIcon } =
    useMissionsTabs({
      props,
      missionTabRef,
    });
  const { battlesCount, missionsCount } = notificationCalculator(
    battlesFeatureState?.missions,
    battlesFeatureState?.currentCycle
  );
  selectedTab.forEach(({ TYPE }) => {
    missionTabRef.current[TYPE] = React.createRef();
  });

  return (
    <TabContainer tutorialStep={tutorialStep} activeTutorial={activeTutorial}>
      {selectedTab.map(({ TYPE, TAB }, index) => {
        const NotificationAvailabe =
          !currentMissionType &&
          ((TYPE === MISSIONS_TYPES.BATTLES && battlesCount > 0 && isOnMissions) ||
            (TYPE === MISSIONS_TYPES.EXPLORER && missionsCount > 0 && isOnBattles));
        return (
          <>
            <Tab
              ref={missionTabRef.current?.[TYPE]}
              id={TYPE}
              key={`tab-${index}`}
              src={TAB}
              onClick={() => handleOnPress(TYPE)}
            />
            {NotificationAvailabe && (
              <NotificationBadge
                count={TYPE === MISSIONS_TYPES.BATTLES ? battlesCount : missionsCount}
                theme={TYPE === MISSIONS_TYPES.BATTLES ? BattlesNotificationStyle : MissionsNotificationStyle}
                textStyle={NotificationTextStyle}
              />
            )}
            {TYPE === MISSIONS_TYPES.BATTLES && !noAvailableGames && battlesTimestamp && (
              <TimerContainer visible={showClockIcon} source={battlesAssets.BATTLES_TOP_BAR_BATTLES_TIMER_PURE_BACKING}>
                <TimerContentContainer>
                  <TimerClockIcon visible={showClockIcon} src={battlesAssets.BATTLES_TAB_TIMER_ICON} />
                  <TimeRemainingText
                    style={{
                      margin: 0,
                      padding: 0,
                      fontSize: isTablet ? '1.5vw' : isMobile ? (isAndroid ? '1.3vw' : '1.2vw') : '1.6vw',
                    }}
                    until={battlesTimestamp}
                    endTimerCallback={hideClockIcon}
                    hideOnFinish
                  />
                </TimerContentContainer>
              </TimerContainer>
            )}
          </>
        );
      })}
    </TabContainer>
  );
};

export default MissionTabs;

const MissionsNotificationStyle = css`
  width: ${isMobile & !isTablet ? 1.5 : 1.8}vw;
  height: ${isMobile & !isTablet ? 1.5 : 1.8}vw;
  top: ${isMobile & !isTablet ? -0.5 : -1}vw;
  border-radius: 1vw;
  right: ${isMobile & !isTablet ? 11.3 : 14.2}vw;
  box-sizing: border-box;
  z-index: 3;
`;
const BattlesNotificationStyle = css`
  width: ${isMobile & !isTablet ? 1.5 : 1.8}vw;
  height: ${isMobile & !isTablet ? 1.5 : 1.8}vw;
  top: ${isMobile & !isTablet ? -0.5 : -1}vw;
  border-radius: 1vw;
  right: -0.8vw;
  box-sizing: border-box;
  z-index: 3;
`;

const NotificationTextStyle = css`
  font-weight: 700;
  font-size: ${isMobile & !isTablet ? 1 : 1.2}vw;
`;

const TimerContentContainer = styled.div`
  display: flex;
  position: absolute;
  height: 2vw;
  width: 14vw;
  justify-content: center;
  align-items: center;
  flex-direction: row;
  gap: 0.5vw;
  top: 2.8vw;
  ${({ theme }) =>
    theme.screens.devices(['mobile-browser', 'mobile-standalone']) &&
    `
     width: 11vw;
     top: 2vw;
  `}
  ${({ theme }) =>
    theme.screens.devices(['tablet-browser', 'tablet-standalone']) &&
    `
     top: 2.9vw;
  `}
`;

const TimerClockIcon = styled.img.withConfig({
  shouldForwardProp: (prop) => !['visible'].includes(prop),
})`
  width: 1.4vw;
  height: 1.4vw;
  opacity: ${({ visible }) => (visible ? 1 : 0)};
  ${({ theme }) =>
    theme.screens.devices(['mobile-browser', 'mobile-standalone']) &&
    `
  width: 1.1vw;
  height: 1.1vw;
  `}
`;

const TabContainer = styled.div.withConfig({
  shouldForwardProp: (prop) => !['tutorialStep', 'activeTutorial'].includes(prop),
})`
  opacity: ${({ tutorialStep, activeTutorial }) => tutorialOpacity(activeTutorial && tutorialStep > 1)};
  position: absolute;
  display: flex;
  flex-direction: row;
  top: 6vw;
  right: 1.2vw;
  gap: 1vw;
  z-index: 10;

  ${({ theme }) => theme.breakpoints.down('xl')} {
    top: 7vw;
  }

  ${({ theme }) => theme.breakpoints.down('lg')} {
    top: 8vw;
  }
  ${({ theme }) => theme.breakpoints.only('md')} {
    top: 7.5vw;
  }

  ${({ theme }) => theme.breakpoints.down('sm')} {
    top: 11vw;
  }

  @media (max-width: 900px) {
    top: 15vw;
  }

  ${({ theme }) =>
    theme.screens.device('mobile-browser') &&
    `
    top: 15.5vh;
    right: 8.6vw;
  `}

  ${({ theme }) =>
    theme.screens.device('mobile-standalone') &&
    `
    top: 15.5vh;
    right: 6.3vw;
  `}

  ${({ theme }) =>
    theme.screens.device('mobile-standalone') &&
    theme.platform('android') &&
    `
    top: 17.2vh;
    right: 5.1vw;
  `}

  ${({ theme }) =>
    theme.screens.device('mobile-browser') &&
    theme.platform('android') &&
    `
    top: 17.2vh;
  `}

  ${({ theme }) =>
    theme.screens.devices(['tablet-browser', 'tablet-standalone']) &&
    `
    top: 12vh;
    right: 1.3vw;
  `}
`;

const Tab = styled.img`
  width: ${isMobile && !isTablet ? 11 : 14}vw;
  object-fit: contain;
  cursor: pointer;
  z-index: 2;
`;

const TimerContainer = styled.div.withConfig({
  shouldForwardProp: (prop) => !['source', 'visible'].includes(prop),
})`
  z-index: 1;
  position: absolute;
  display: flex;
  background: url(${({ source }) => source}) no-repeat;
  background-size: contain;
  background-position: center;
  width: 14vw;
  height: 6vw;
  right: 0;
  opacity: ${({ visible }) => (visible ? 1 : 0)};
  justify-content: flex-end;
  align-items: center;
  ${({ theme }) =>
    theme.screens.devices(['mobile-browser', 'mobile-standalone']) &&
    `
    width: 11vw;
    height: 4.8vw;
  `}
`;

